
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
} from "@/components/ui/navigation-menu";
import { Menu, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from '@/components/ui/ThemeToggle';
import { usePortalResolver } from '@/hooks/usePortalResolver';
import { saveRedirectPath } from '@/utils/auth-redirect';

const Navbar = () => {
  const { user, activeRole, isLoading, getUserType, signOut } = useAuth();
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { preferredPortal, resolvePortal } = usePortalResolver();
  const isPasswordRecoveryRoute =
    location.pathname.startsWith('/reset-password') || location.pathname.startsWith('/forgot-password');
  const isRecoverySessionActive = (() => {
    try {
      const raw = sessionStorage.getItem('password_recovery_in_progress');
      if (!raw) return false;
      const parsed = JSON.parse(raw) as { ts?: number };
      if (!parsed.ts) return false;
      return Date.now() - parsed.ts < 10 * 60 * 1000;
    } catch {
      return false;
    }
  })();
  const hideAuthenticatedActions = isPasswordRecoveryRoute || isRecoverySessionActive;

  const handleDashboardRedirect = () => {
    if (!user || isLoading) return;

    const portal = preferredPortal ?? 'unknown';

    if (activeRole === 'admin') {
      navigate('/admin/dashboard');
      return;
    }
    if (activeRole === 'host' || portal === 'host') {
      navigate('/space/dashboard');
      return;
    }
    if (activeRole === 'practitioner' || portal === 'practitioner') {
      navigate('/practitioner-dashboard');
      return;
    }
    if (activeRole === 'client' || portal === 'client') {
      navigate('/client-dashboard');
      return;
    }

    void resolvePortal();
    navigate('/client-dashboard');
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const isPathActive = (path: string) => {
    return location.pathname === path;
  };

  const navigationLinks = [
    { path: '/booking-choice', label: t('bookAppointment') },
    { path: '/framework', label: t('certifications') },
    { path: '/community', label: 'Community' },
    
    { path: '/events', label: t('events', { defaultValue: 'Events' }) },
  ];

  const renderNavLinks = () => (
    navigationLinks.map((link) => (
      <NavigationMenuItem key={link.path}>
        <Link to={link.path} className={cn(
          "text-warm-700 hover:text-gold-ink px-2 lg:px-3 py-2 text-sm lg:text-base font-medium whitespace-nowrap",
          isPathActive(link.path) && "text-gold-ink font-semibold"
        )}>
          {link.label}
        </Link>
      </NavigationMenuItem>
    ))
  );

  const renderMobileMenuItems = () => (
    navigationLinks.map((link) => (
      <SheetClose key={link.path} asChild>
        <Link 
          to={link.path} 
          className={cn(
            "block py-3 px-4 text-lg border-b border-warm-200 transition-colors",
            isPathActive(link.path) 
              ? "text-gold-ink font-semibold" 
              : "text-warm-700 hover:text-gold-ink"
          )}
        >
          {link.label}
        </Link>
      </SheetClose>
    ))
  );

  return (
    <nav className="bg-white dark:bg-[hsl(var(--background))] bg-opacity-95 border-b border-warm-200 dark:border-[hsl(var(--border))] px-3 sm:px-6 py-3 fixed w-full z-[80]" role="navigation" aria-label="Main navigation">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-1 sm:space-x-2 shrink-0">
          <Link to="/" className="flex items-center space-x-1 sm:space-x-2" aria-label="Resonow homepage">
            <img
              src="/lovable-uploads/ba1e90a0-0271-4ea2-80f0-40d8acccb492.png"
              alt="Resonow - Professional healing platform logo"
              className="h-5 w-5 sm:h-6 sm:w-6"
            />
            <span className="text-base sm:text-xl font-brand text-gold-ink">Resonow</span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        {!isMobile && (
          <div className="flex items-center space-x-4 lg:space-x-6">
            <NavigationMenu>
              <NavigationMenuList className="space-x-2 lg:space-x-4">
                {renderNavLinks()}
              </NavigationMenuList>
            </NavigationMenu>

            <div className="flex items-center space-x-3">
              <ThemeToggle />
              <LanguageSwitcher />
              
              
              {!user && !isPasswordRecoveryRoute && (
                <Button
                onClick={() => { saveRedirectPath(); navigate('/client-auth'); }}
                variant="outline"
                size="sm"
                className="border-brand-500 text-gold-ink hover:bg-brand-50 font-medium text-sm px-4 py-2 min-h-[40px] min-w-[100px]"
                aria-label="Login or sign up to Resonow"
                >
                  {t('loginSignUp')}
                </Button>
              )}

              {user && !hideAuthenticatedActions && (
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={handleDashboardRedirect}
                    variant="outline" 
                    size="sm"
                    className="border-brand-500 text-gold-ink hover:bg-brand-50 font-medium text-sm px-4 py-2 min-h-[40px]"
                    aria-label="Go to my dashboard"
                  >
                    {t('myDashboard')}
                  </Button>
                  <Button
                    onClick={handleSignOut}
                    variant="ghost"
                    size="sm"
                    className="text-warm-700 hover:text-semantic-error font-medium text-sm px-3 py-2 min-h-[40px]"
                    aria-label="Logout from Resonow"
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    Logout
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Navigation with Sheet/Drawer */}
        {isMobile && (
          <div className="flex items-center space-x-2">
            <ThemeToggle />
            {!user && !isPasswordRecoveryRoute && (
              <Button
                onClick={() => { saveRedirectPath(); navigate('/client-auth'); }}
                variant="outline"
                size="sm"
                className="border-brand-500 text-gold-ink hover:bg-brand-50 font-medium text-sm px-3 py-2 min-h-[44px] min-w-[80px]"
              >
                {t('common:login')}
              </Button>
            )}

            {user && !hideAuthenticatedActions && (
              <div className="flex items-center space-x-2">
                <Button
                  onClick={handleDashboardRedirect}
                  variant="outline"
                  size="sm"
                  className="border-brand-500 text-gold-ink hover:bg-brand-50 font-medium text-xs px-2 py-1.5 min-h-[36px]"
                >
                  Dashboard
                </Button>
                <Button
                  onClick={handleSignOut}
                  variant="ghost"
                  size="icon"
                  className="text-warm-700 hover:text-semantic-error min-h-[44px] min-w-[44px]"
                  aria-label="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px] p-0" aria-label="Open mobile navigation menu">
                  <Menu className="h-6 w-6 text-brand-700" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[320px]" role="dialog" aria-labelledby="mobile-menu-title">
                <SheetHeader className="mb-6">
                  <SheetTitle id="mobile-menu-title" className="text-brand-700 text-lg">{t('menu')}</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col space-y-2">
                  {renderMobileMenuItems()}
                  
                  <div className="border-t border-warm-200 my-4"></div>
                  
                  <div className="mb-4">
                    <LanguageSwitcher />
                  </div>
                  
                  {!isPasswordRecoveryRoute && user ? (
                    <div className="space-y-3">
                      <SheetClose asChild>
                        <Button
                          onClick={handleDashboardRedirect}
                          variant="ghost"
                          className="justify-start text-left text-brand-700 w-full h-12 px-4 font-medium"
                        >
                          {t('myDashboard')}
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button
                          onClick={handleSignOut}
                          variant="ghost"
                          className="justify-start text-left text-semantic-error hover:text-red-700 w-full h-12 px-4 font-medium"
                        >
                          <LogOut className="mr-3 h-4 w-4" />
                          Logout
                        </Button>
                      </SheetClose>
                    </div>
                  ) : !isPasswordRecoveryRoute ? (
                    <SheetClose asChild>
                      <Button
                        onClick={() => { saveRedirectPath(); navigate('/client-auth'); }}
                        variant="ghost"
                        className="justify-start text-left text-brand-700 w-full h-12 px-4 font-medium"
                      >
                        {t('loginSignUp')}
                      </Button>
                    </SheetClose>
                  ) : null}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
